import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Globe } from "@digital-twins/design-system/icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { ROOM_LANGUAGES } from "@/lib/constants/languages";
import { chipStrings } from "./strings";

/**
 * LanguageChip — inline replacement for the upfront LanguageSelectionModal.
 * A popover select in the flow header (defaults to English); same chip visual
 * family as GoalChip. Language changes land directly in creation state via
 * onLanguageChange. The compact variant renders just the globe + language
 * code for dense toolbars. In the questionnaire builder the full chip sits
 * above the workspace title, left-aligned.
 */

interface LanguageChipProps {
  language: string;
  onLanguageChange: (lang: string) => void;
  compact?: boolean;
  /** Shows the language but offers no change. */
  disabled?: boolean;
  className?: string;
  popoverAlign?: "start" | "center" | "end";
  popoverSide?: "top" | "right" | "bottom" | "left";
  /** Radix collision padding (px) when flipping away from viewport edges. */
  popoverCollisionPadding?: number;
}

export function LanguageChip({
  language,
  onLanguageChange,
  compact,
  disabled,
  className,
  popoverAlign = "start",
  popoverSide = "bottom",
  popoverCollisionPadding = 12,
}: LanguageChipProps) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const current = ROOM_LANGUAGES.find((lang) => lang.code === language);
  const currentName = current
    ? t(`languageNames.${current.code}`)
    : t("languageNames.en");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="language-chip"
          aria-label={chipStrings.languageAriaLabel}
          title={currentName}
          disabled={disabled}
          className={cn(
            "inline-flex items-center transition-colors",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            "disabled:cursor-not-allowed disabled:opacity-50",
            compact
              ? "gap-1 rounded-md px-1.5 py-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              : "gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-sm text-foreground hover:border-primary/40",
            className,
          )}
        >
          <Globe
            className={cn(
              compact ? "h-3.5 w-3.5" : "h-4 w-4 text-muted-foreground",
            )}
          />
          {compact ? (
            <span className="text-[11px] font-medium uppercase leading-none tracking-wide">
              {current?.code ?? "en"}
            </span>
          ) : (
            <span className="whitespace-nowrap leading-none">
              {currentName}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align={popoverAlign}
        side={popoverSide}
        collisionPadding={popoverCollisionPadding}
        className="max-h-72 w-56 overflow-y-auto p-1"
      >
        {ROOM_LANGUAGES.map((lang) => (
          <button
            key={lang.code}
            type="button"
            data-testid={`language-chip-option-${lang.code}`}
            onClick={() => {
              onLanguageChange(lang.code);
              setOpen(false);
            }}
            className={cn(
              "flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-sm text-foreground",
              "transition-colors hover:bg-muted focus-visible:outline-none focus-visible:bg-muted",
            )}
          >
            {t(`languageNames.${lang.code}`)}
            {lang.code === language && <Check className="h-4 w-4 text-primary" />}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
